import { LoadingSpinner } from "@/shared/components/custom/spin";
import { tierMappingsQueryKeys } from "@/shared/constants/QueryKeys";
import { useIsAdmin } from "@/shared/hooks/use-is-admin";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Select, SelectItem } from "@nextui-org/react";
import {
  getTierMappingsList,
  updateTierMapping,
  type TierMapping,
  type UpdateTierMappingPayload,
} from "../../requests/tier-mappings";

const TierMappingCard = ({ tier }: { tier: TierMapping }) => {
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTierMappingPayload }) =>
      updateTierMapping(id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tierMappingsQueryKeys.all });
    },
    onError: () => {
      addNotification({
        type: "error",
        title: "Failed to update tier mapping",
        message: "Something went wrong. Please try again later!",
      });
    },
  });

  const handleQuestionnaireChange = (keys: "all" | Set<string>) => {
    const selectedIds = keys === "all" ? [] : Array.from(keys);
    updateMutation.mutate({
      id: tier.id,
      payload: {
        questionnaire_ids: selectedIds,
        control_set_ids: tier.control_sets.map((cs) => cs.id),
      },
    });
  };

  const handleControlSetChange = (keys: "all" | Set<string>) => {
    const selectedIds = keys === "all" ? [] : Array.from(keys);
    updateMutation.mutate({
      id: tier.id,
      payload: {
        questionnaire_ids: tier.questionnaires.map((q) => q.id),
        control_set_ids: selectedIds,
      },
    });
  };

  return (
    <div
      style={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '16px',
        gap: '12px',
        width: '472px',
        background: '#FFFFFF',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
        borderRadius: '8px',
        flex: 'none',
        flexGrow: 0,
      }}
    >
      {/* Tier name header */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          padding: '0px',
          gap: '6px',
          height: '20px',
          alignSelf: 'stretch',
          flex: 'none',
          order: 0,
          flexGrow: 0,
        }}
      >
        <span
          style={{
            fontFamily: 'Inter',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            color: '#0F172B',
            flex: 'none',
            order: 0,
            flexGrow: 0,
          }}
        >
          {tier.tier_name}
        </span>
      </div>

      {/* Fields container */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '12px',
          gap: '8px',
          background: '#F8FAFC',
          borderRadius: '4px',
          alignSelf: 'stretch',
          flex: 'none',
          order: 2,
          flexGrow: 0,
        }}
      >
        {/* Questionnaire & Document Sets field */}
        <Select
          label="Questionnaire & Document Sets"
          placeholder="Select questionnaire & document sets"
          labelPlacement="outside"
          selectionMode="single"
          selectedKeys={new Set(tier.questionnaires.map((q) => q.id))}
          isDisabled={updateMutation.isPending}
          classNames={{
            label: "text-xs text-[#45566C] font-normal",
            trigger: "w-full shadow-none rounded-md border-1 border-gray-300 min-h-[36px] h-9",
            listboxWrapper: "custom-dropdown-scrollbar",
          }}
          onSelectionChange={(keys) => handleQuestionnaireChange(keys as "all" | Set<string>)}
          renderValue={(items) => {
            if (!items || items.length === 0) return null;
            const item = items[0];
            const option = tier.organization_available_questionnaires.find((q) => q.id === item.key);
            return <span>{option?.name || item.textValue}</span>;
          }}
        >
          {tier.organization_available_questionnaires.map((q) => (
            <SelectItem key={q.id} value={q.id} textValue={q.name}>
              {q.name}
            </SelectItem>
          ))}
        </Select>

        {/* Control Sets field */}
        <Select
          label="Control Sets"
          placeholder="Select control sets"
          labelPlacement="outside"
          selectionMode="multiple"
          selectedKeys={new Set(tier.control_sets.map((cs) => cs.id))}
          isDisabled={updateMutation.isPending}
          classNames={{
            label: "text-xs text-[#45566C] font-normal",
            trigger: "w-full shadow-none rounded-md border-1 border-gray-300 min-h-[36px] h-9",
            listboxWrapper: "custom-dropdown-scrollbar",
          }}
          onSelectionChange={(keys) => handleControlSetChange(keys as "all" | Set<string>)}
          renderValue={(items) => {
            if (!items || items.length === 0) return null;
            if (items.length === 1) {
              const item = items[0];
              const option = tier.organization_available_control_sets.find((cs) => cs.id === item.key);
              return <span>{option?.name || item.textValue}</span>;
            }
            const firstItem = items[0];
            const firstOption = tier.organization_available_control_sets.find((cs) => cs.id === firstItem.key);
            return (
              <span>
                {firstOption?.name || firstItem.textValue}{" "}
                <span className="text-[#006FEE]">+{items.length - 1} more</span>
              </span>
            );
          }}
        >
          {tier.organization_available_control_sets.map((cs) => (
            <SelectItem key={cs.id} value={cs.id} textValue={cs.name}>
              {cs.name}
            </SelectItem>
          ))}
        </Select>
      </div>
    </div>
  );
};

const TierMappingPage = () => {
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();

  const {
    data: tierMappings = [],
    isLoading,
    error: fetchError,
    isError,
  } = useQuery({
    queryKey: tierMappingsQueryKeys.all,
    queryFn: getTierMappingsList,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!isError) return;
    const errorMessage =
      fetchError instanceof Error ? fetchError.message : "Something went wrong. Please try again later!";

    addNotification({
      type: "error",
      title: "Failed to fetch tier mappings",
      message: errorMessage,
    });
  }, [isError, fetchError]);

  if (!isAdmin) {
    return <Navigate to={`/${ROUTES.SETTINGS}`} state={{ tab: "general" }} replace />;
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex items-center px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/${ROUTES.SETTINGS}`, { state: { tab: "third-party" }, replace: true })}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-sm font-medium text-gray-900">Tier Mapping</h1>
        </div>
      </div>
      <div className="grow w-full overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <LoadingSpinner containerClassName="bg-transparent relative" className="mx-auto" />
          </div>
        ) : tierMappings.length === 0 ? (
          <div className="flex items-center justify-center h-40 text-sm text-gray-500">
            No tier mappings found
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: '16px',
              alignItems: 'flex-start',
              justifyContent: 'center',
            }}
          >
            {tierMappings.map((tier: TierMapping) => (
              <TierMappingCard key={tier.id} tier={tier} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TierMappingPage;
